import React, { useState, useRef, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Button from '@mui/material/Button';
import { FormattedMessage, injectIntl } from 'react-intl';
import inlineStyles from './styles';
import messages from './messages';
import ShoppingSearch from '../../../../Shopping/ShoppingSearch/ShoppingSearch';
import { TRIP_TYPE_SINGLE } from '../../../../Shopping/util';
import ExchangeOrderTitle from '../../../components/ExchangeOrderDialog/ExchangeOrderTitle';
import { getJourneysListApi } from '../../../apiBooking';
import ExtendedSnackbar from '../../../../components/ExtendedSnackbar/ExtendedSnackbar';
import TrainJourneySelector from './components/TrainJourneySelector/TrainJourneySelector';
import RefreshIndicator from '../../../../components/RefreshIndicator/RefreshIndicator';
import { cleanDate } from '../../../../utils/datetimeUtils';
import { dateFormat } from '../../../../Shopping/apiShopping';
import { clearAmendmentsData } from '../../actionsManageBooking';

const timeFormat = {
	hour: 'numeric',
	minute: 'numeric',
	hour12: false,
};

const StyledDialog = styled(Dialog)(() => ({
	'& .MuiPaper-root': {
		...inlineStyles.dialogPaper,
	},
}));

const extractLocations = (superOrders) => {
	const locations = [];

	superOrders.forEach(([superOrderId, orders]) => {
		orders.forEach((order) => {
			if (order && typeof order === 'object') {
				const departure = order.BookingOrderDepartureLocation;
				const destination = order.BookingOrderDestination;
				if (departure && destination) {
					locations.push({ superOrderId, departure, destination });
				}
			}
		});
	});

	return locations;
};

const AmendSeatReservationsDialog = ({
	superOrders,
	open,
	handleClose,
	booking,
	passengers,
	queryItems,
	orderAccount,
	ticketableFareGroup,
	amendmentsData,
	departDate,
	arriveDate,
	legSelectionId,
	intl,
	resetAmendmentsData,
}) => {
	const [displayShoppingSearch, setDisplayShoppingSearch] = useState(true);
	const [showTrainJourneys, setShowTrainJourneys] = useState(false);
	const [trainJourneysData, setTrainJourneysData] = useState(null);
	const [errorText, setErrorText] = useState('');
	const [isFetching, setIsFetching] = useState(false);
	const [confirmSeatsData, setConfirmSeatsData] = useState({});

	const searchCompRef = useRef(null);

	const locations = extractLocations(superOrders);
	const bookingOrder = booking.BookingOrders[0];

	const amendmentInfo = bookingOrder?.BookingOrderSeatReservationAmendmentInfo;
	const { BookingOrderSeatReservationAmendmentLegs } = amendmentInfo || {};
	const {
		BookingOrderSeatReservationAmendmentLegCanAdd = false,
		BookingOrderSeatReservationAmendmentLegCanChange = false,
	} = BookingOrderSeatReservationAmendmentLegs?.[0] ?? {};

	// legSelectionId not added to dependency array intentionally,
	const journeyDetail = useMemo(
		() =>
			amendmentsData.BookingOrderSeatReservationAmendmentInfo
				.BookingOrderSeatReservationAmendmentLegs[legSelectionId - 1],
		[],
	);

	const handleSearchAmendSeatReservationsJourneys = () => {
		setIsFetching(true);
		let tempDepartDate = '';
		let tempDepartTime = '';
		let tempArriveDate = '';
		let tempArriveTime = '';

		if (departDate) {
			tempDepartDate = cleanDate(intl.formatDate(departDate, dateFormat));
			tempDepartTime = cleanDate(intl.formatTime(departDate, timeFormat));
		}
		if (arriveDate) {
			tempArriveDate = cleanDate(intl.formatDate(arriveDate, dateFormat));
			tempArriveTime = cleanDate(intl.formatTime(arriveDate, timeFormat));
		} else {
			[tempArriveDate, tempArriveTime] = [tempDepartDate, tempDepartTime];
		}

		const data = {
			shoppingContext: orderAccount.value,
			recordLocator: queryItems.srtRecLocator,
			orderLocator: bookingOrder.BookingOrderID,
			ticketableFareLocators:
				ticketableFareGroup.BookingDetailsLegTicketableFareIds,
			legSolutionId: legSelectionId, // 1 or 2 are the expected allowed values
			departCode: journeyDetail.BookingDepartStationCode,
			departDate: tempDepartDate,
			departTime: tempDepartTime,
			arriveCode: journeyDetail.BookingArriveStationCode,
			arriveDate: tempArriveDate,
			arriveTime: tempArriveTime,
		};

		getJourneysListApi(
			data,
			(response) => {
				const errorMsg = response?.errorResponse?.message;
				setErrorText(errorMsg);
				setIsFetching(false);
			},
			(success) => {
				setConfirmSeatsData({
					DataStorageId: success.successResponse.data.DataStorageId,
					shoppingContext: orderAccount.value,
					recordLocator: queryItems.srtRecLocator,
					orderLocator: bookingOrder.BookingOrderID,
					ticketableFareLocators: [
						ticketableFareGroup.BookingDetailsLegTicketableFareIds,
					],
				});
				setTrainJourneysData(success.successResponse);
				setDisplayShoppingSearch(false);
				setShowTrainJourneys(true);
				setIsFetching(false);
			},
		);
	};

	useEffect(
		() => () => {
			resetAmendmentsData();
		},
		[],
	);

	const renderActions = () => {
		if (displayShoppingSearch) {
			return (
				<div className="row" style={inlineStyles.dialogActionsRoot}>
					<div
						className="col-4 offset-4 offset-md-8"
						style={inlineStyles.dialogActionsShoppingSearch}
					>
						<Button
							variant="contained"
							id="srtAmendSeatReservationsClose"
							onClick={handleClose}
							fullWidth
						>
							<FormattedMessage {...messages.btnClose} />
						</Button>
						<Button
							variant="contained"
							id="srtAmendSeatReservationsClose"
							onClick={() => {
								handleSearchAmendSeatReservationsJourneys();
							}}
							fullWidth
							color="primary"
							disabled={isFetching}
						>
							<FormattedMessage
								{...messages.lblAmendSeatReservationsDialogSearchForTrains}
							/>
						</Button>
					</div>
				</div>
			);
		}

		return (
			<div className="row" style={inlineStyles.dialogActionsRoot}>
				<div className="col-4 offset-4 offset-md-8">
					<Button
						variant="contained"
						id="srtAmendSeatReservationsClose"
						onClick={handleClose}
						fullWidth
					>
						<FormattedMessage {...messages.btnClose} />
					</Button>
				</div>
			</div>
		);
	};

	const handleChangeJourneyAndReservationsClick = () => {
		setDisplayShoppingSearch(true);
	};

	const renderJourneyFlows = () => {
		if (displayShoppingSearch) {
			const selectedFareQualifiers = [];
			const supplyChannelCode = '';

			const departStationData = {
				name: journeyDetail.BookingDepartStationName,
				code: journeyDetail.BookingDepartStationCode,
				supplier: journeyDetail.BookingDepartStationSupplierCodes.join(','),
				countryCode: journeyDetail.BookingDepartStationCountryCode,
				departDate: journeyDetail.BookingDepartDateTime,
				bookingEndDateTimeRestriction:
					journeyDetail.BookingEndDateTimeRestriction,
				bookingStartDateTimeRestriction:
					journeyDetail.BookingStartDateTimeRestriction,
			};

			const arriveStationData = {
				name: journeyDetail.BookingArriveStationName,
				code: journeyDetail.BookingArriveStationCode,
				supplier: journeyDetail.BookingArriveStationSupplierCodes.join(','),
				countryCode: journeyDetail.BookingArriveStationCountryCode,
				arriveDate: journeyDetail.BookingArriveDateTime,
			};

			const bookingOrderId = bookingOrder.BookingOrderID;

			return (
				<div>
					<ExchangeOrderTitle
						style={{ width: '100%' }}
						exchangeableTicketableFareIds={
							ticketableFareGroup.BookingDetailsLegTicketableFareIds
						}
						isSeasonTrip={false}
						ticketableFareGroups={[
							{
								...ticketableFareGroup,
								departStationLongName: journeyDetail.BookingDepartStationName,
								arriveStationLongName: journeyDetail.BookingArriveStationName,
							},
						]}
					/>

					<ShoppingSearch
						isAmend
						innerRef={searchCompRef}
						exchangeOrderMode
						amendSeatReservationMode
						orderPassengers={passengers}
						orderAccount={orderAccount}
						departStationData={departStationData}
						arriveStationData={arriveStationData}
						recordLocator={queryItems.srtRecLocator}
						orderId={bookingOrderId}
						exchangeOrderTripType={TRIP_TYPE_SINGLE}
						selectedFareQualifiers={selectedFareQualifiers}
						supplyChannelCode={supplyChannelCode}
					/>
				</div>
			);
		}

		if (showTrainJourneys && trainJourneysData) {
			const journeys =
				trainJourneysData.data.ShoppingLegs[0].ShoppingLegSolutions;
			return (
				<div>
					<ExchangeOrderTitle
						exchangeableTicketableFareIds={
							ticketableFareGroup.BookingDetailsLegTicketableFareIds
						}
						isSeasonTrip={false}
						ticketableFareGroups={[
							{
								...ticketableFareGroup,
								departStationLongName: journeyDetail.BookingDepartStationName,
								arriveStationLongName: journeyDetail.BookingArriveStationName,
							},
						]}
						style={{ width: '100%' }}
					/>

					<TrainJourneySelector
						journeys={journeys}
						trainJourneysData={trainJourneysData}
						ticketableFareGroups={[ticketableFareGroup]}
						confirmSeatsData={confirmSeatsData}
						refreshBookingParams={queryItems}
						isAmend
						closeAllDialogs={() => {
							setDisplayShoppingSearch(false);
							handleClose();
						}}
						legSolutionId={legSelectionId}
					/>
				</div>
			);
		}

		return (
			<div>
				<div>
					<div>
						{locations?.length && (
							<h2 style={inlineStyles.h2}>
								{locations[0].departure}
								<ArrowForwardIcon style={inlineStyles.arrow} />
								{locations[0].destination}
							</h2>
						)}
					</div>
				</div>
				<div>
					<div style={inlineStyles.seatButtons}>
						<Button
							variant="contained"
							id="srtChangeSeats"
							color="primary"
							fullWidth
							disabled={!BookingOrderSeatReservationAmendmentLegCanChange}
							onClick={() => null}
						>
							<FormattedMessage
								{...messages.lblAmendSeatReservationsDialogChangeSeat}
							/>
						</Button>

						<Button
							variant="contained"
							id="srtAddSeats"
							color="primary"
							fullWidth
							disabled={!BookingOrderSeatReservationAmendmentLegCanAdd}
							onClick={() => null}
						>
							<FormattedMessage
								{...messages.lblAmendSeatReservationsDialogAddSeat}
							/>
						</Button>
					</div>
					<div>
						<Button
							variant="contained"
							id="srtChangeJourneyAndReservations"
							color="primary"
							fullWidth
							onClick={handleChangeJourneyAndReservationsClick}
						>
							<FormattedMessage
								{...messages.lblAmendSeatReservationsDialogChangeTrainAndSeat}
							/>
						</Button>
					</div>
				</div>
			</div>
		);
	};

	return (
		<div>
			<StyledDialog
				open={open}
				onClose={() => {
					setDisplayShoppingSearch(false);
					handleClose();
				}}
				disableEnforceFocus
				maxWidth={false}
			>
				<DialogTitle>
					<FormattedMessage {...messages.lblAmendSeatReservationsDialogTitle} />
				</DialogTitle>
				<DialogContent>{renderJourneyFlows()}</DialogContent>
				<DialogActions>{renderActions()}</DialogActions>
			</StyledDialog>
			<ExtendedSnackbar
				id="srtAmendSeatReservationsSnackBar"
				open={errorText !== ''}
				message={errorText}
				onClose={() => setErrorText('')}
			/>
			<div className="container-fluid">
				<RefreshIndicator
					size={36}
					top={0}
					left={0}
					status={isFetching ? 'loading' : 'hide'}
					style={{
						marginTop: '10%',
						marginLeft: '45%',
					}}
				/>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	const departDate = state.getIn(['shopping', 'query', 'departDate']);
	const arriveDate = state.getIn(['shopping', 'query', 'arriveDate']);
	const amendmentsData = state.getIn(['booking', 'amendmentsData']).toJS();

	return {
		departDate: departDate ? new Date(departDate) : departDate,
		arriveDate: arriveDate ? new Date(arriveDate) : arriveDate,
		amendmentsData,
	};
};

AmendSeatReservationsDialog.propTypes = {
	superOrders: PropTypes.array,
	open: PropTypes.bool.isRequired,
	handleClose: PropTypes.func.isRequired,
	booking: PropTypes.object,
	passengers: PropTypes.array,
	queryItems: PropTypes.object,
	orderAccount: PropTypes.object,
	orderId: PropTypes.string,
	ticketableFareGroup: PropTypes.object,
	amendmentsData: PropTypes.object,
	departDate: PropTypes.object,
	arriveDate: PropTypes.object,
	intl: PropTypes.object,
	legSelectionId: PropTypes.number,
	resetAmendmentsData: PropTypes.func,
};

const mapDispatchToProps = {
	resetAmendmentsData: clearAmendmentsData,
};

export default connect(
	mapStateToProps,
	mapDispatchToProps,
)(injectIntl(AmendSeatReservationsDialog));
